import React, { useState, ChangeEvent, useEffect } from 'react'
import { Modal, Button, Form, ProgressBar, Spinner, Row, Col } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useUploadFile } from '../../../hooks/useUploadFile'
import { CREATE_RESOURCE_CENTER_STR } from '../../../gql/mutations/resourceCenterMutations'
import { toast } from 'react-toastify'
import { ICreateResourceCenterResponse } from '../../../types/resourceCenter'
import { IntlShape, useIntl, FormattedMessage } from 'react-intl'
import { CAMPAIGN_MESSAGES as M, formatPercent } from '../campaignMessages'

interface CampaignMediaUploadModalProps {
    isOpen: boolean
    onClose: () => void
    refetch: () => void
}

/** El schema recibe `intl` para que los mensajes salgan del catálogo (guía §17.8). */
const buildValidationSchema = (intl: IntlShape) =>
    Yup.object().shape({
        name: Yup.string().required(intl.formatMessage(M.validationNameRequired)),
        file: Yup.mixed().required(intl.formatMessage(M.validationFileRequired)),
    })

export const CampaignMediaUploadModal: React.FC<CampaignMediaUploadModalProps> = ({
    isOpen,
    onClose,
    refetch,
}) => {
    const intl = useIntl()
    const validationSchema = buildValidationSchema(intl)
    const [uploadFile, { data, error, loading }] = useUploadFile<ICreateResourceCenterResponse>()
    const [uploadProgress, setUploadProgress] = useState(0)
    const [preview, setPreview] = useState<string | null>(null)

    const formik = useFormik({
        initialValues: {
            name: '',
            file: null as File | null,
            isActive: true, // "Visible for others" / Public
        },
        validationSchema,
        onSubmit: async (v) => {
            if (!v.file) return

            setUploadProgress(0)
            const formdata = new FormData()

            // File is always mapped to '0'
            formdata.append('0', v.file, v.file.name)

            // GraphQL map for upload
            const map = { '0': ['variables.resourceData.file'] }

            // infer resourceType from mime type if possible, else default to 'document'
            // For campaigns media, we mostly care about images, but let's be safe.
            let resourceType = 'document'
            if (v.file.type.startsWith('image/')) resourceType = 'image'
            else if (v.file.type.startsWith('video/')) resourceType = 'video'

            const operations = {
                query: CREATE_RESOURCE_CENTER_STR,
                variables: {
                    resourceData: {
                        name: v.name,
                        file: null, // processed by upload scalar
                        isActive: v.isActive,
                        isCampaignMedia: true, // Mark as campaign media to separate from Resource Center
                        resourceType: resourceType,
                        // Defaults for other unrelated fields
                        description: '',
                        order: 0,
                    },
                },
            }

            formdata.append('map', JSON.stringify(map))
            formdata.append('operations', JSON.stringify(operations))

            try {
                setUploadProgress(30)
                await uploadFile(formdata)
                setUploadProgress(100)
            } catch (err) {
                console.error('Upload failed', err)
                toast.error(intl.formatMessage(M.toastUploadFailed))
                setUploadProgress(0)
            }
        },
    })

    // Auto-generate name from file
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0]
        if (file) {
            formik.setFieldValue('file', file)
            if (!formik.values.name) {
                // remove extension for default name
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "")
                formik.setFieldValue('name', nameWithoutExt)
            }
            // Preview
            if (file.type.startsWith('image/')) {
                const url = URL.createObjectURL(file)
                setPreview(url)
            } else {
                setPreview(null)
            }
        }
    }

    // Success/Error handling
    useEffect(() => {
        if (data?.createResourceCenter?.success) {
            toast.success(intl.formatMessage(M.toastMediaUploaded))
            refetch()
            handleClose()
        } else if (data?.createResourceCenter?.errors?.length) {
            toast.error(
                intl.formatMessage(M.errorWithMessage, {
                    message: data.createResourceCenter.errors[0].message,
                })
            )
        }
    }, [data])

    useEffect(() => {
        if (error) toast.error(intl.formatMessage(M.errorWithMessage, { message: String(error) }))
    }, [error])

    const handleClose = () => {
        formik.resetForm()
        setPreview(null)
        setUploadProgress(0)
        onClose()
    }

    return (
        <Modal show={isOpen} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title><FormattedMessage {...M.mediaModalTitle} /></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="mb-3">
                        <ProgressBar now={uploadProgress} label={formatPercent(uploadProgress)} animated variant="success" />
                    </div>
                )}
                <Form onSubmit={formik.handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label><FormattedMessage {...M.mediaFile} /></Form.Label>
                        <Form.Control
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*,video/*,application/pdf"
                            isInvalid={!!formik.errors.file && !!formik.touched.file}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.file}</Form.Control.Feedback>
                        {preview && (
                            <div className="mt-2 text-center bg-light p-2 rounded">
                                <img src={preview} alt={intl.formatMessage(M.preview)} style={{ maxHeight: '150px', maxWidth: '100%' }} />
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label><FormattedMessage {...M.name} /></Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={intl.formatMessage(M.mediaNamePlaceholder)}
                            {...formik.getFieldProps('name')}
                            isInvalid={!!formik.errors.name && !!formik.touched.name}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="isActive"
                            label={intl.formatMessage(M.mediaVisible)}
                            checked={formik.values.isActive}
                            onChange={formik.handleChange}
                        />
                        <Form.Text className="text-muted">
                            <FormattedMessage {...M.mediaVisibleHint} />
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    <FormattedMessage {...M.cancel} />
                </Button>
                <Button variant="primary" onClick={() => formik.handleSubmit()} disabled={loading}>
                    {loading ? <Spinner size="sm" animation="border" /> : intl.formatMessage(M.mediaUploadAction)}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
